import React, { useState, useCallback, useEffect } from "react";
import { IoCloseOutline } from "react-icons/io5";
import PoolForm from "./PoolForm";
import { savePoolAndInvite, updatePoolDetails, canEditRecord, validateFormData } from "./PoolModalHelper";
import PoolModalNavBar from "./PoolModalNavBar";
import ManagePoolEntries from "./ManagePoolEntries";
import InviteToPool from "./InviteToPool";
import { logError } from "../Utilities/errorLogger";

const ModalContent = React.memo(function ModalContent({
  handleSave, handleClose, poolData, mode, isPoolOwner, setFormData,
  currentTab, setCurrentTab, errors, setErrors, onTeamStatusChange,
  onAddEmail, onSendInvites, isSubmitting, setCanEdit
}) {
  const canEdit = canEditRecord(poolData);

  React.useEffect(() => { setCanEdit(canEdit); }, [canEdit, setCanEdit]);

  const canInvitePlayers = poolData?.tournament_status === "Not Started" ||
    (poolData?.tournament_status !== "Not Started" && poolData?.can_invite_late_players);

  const tabs = [
    { id: "edit", label: "Edit Pool", enabled: true },
    { id: "manage", label: "Manage Entries", enabled: true },
    { id: "invite", label: "Invite Players", enabled: canInvitePlayers },
  ];

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mx-6 mt-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all ${
              currentTab === tab.id
                ? "bg-white text-primary shadow-sm"
                : tab.enabled
                  ? "text-gray-500 hover:text-gray-700"
                  : "text-gray-300 cursor-not-allowed"
            }`}
            onClick={() => tab.enabled && setCurrentTab(tab.id)}
            disabled={!tab.enabled}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {currentTab === "edit" && (
        <PoolForm
          handleClose={handleClose} poolData={poolData} mode={mode}
          isPoolOwner={isPoolOwner} setFormData={setFormData} canEdit={canEdit}
          setCanEdit={setCanEdit} activeTab={currentTab} errors={errors} setErrors={setErrors}
        />
      )}
      {currentTab === "manage" && (
        <ManagePoolEntries poolId={poolData.id} canEdit={canEdit} onTeamStatusChange={onTeamStatusChange} />
      )}
      {currentTab === "invite" && canInvitePlayers && (
        <InviteToPool poolId={poolData.id} onAddEmail={onAddEmail} onSendInvites={onSendInvites} isSubmitting={isSubmitting} onClose={handleClose} />
      )}
    </div>
  );
});

const ContentModal = ({ isOpen, onClose, poolData, mode, refreshData, loggedInUser, onTeamStatusChange }) => {
  const isPoolOwner = poolData && poolData.owner != null && poolData.owner === Number(loggedInUser);
  const [currentTab, setCurrentTab] = useState("edit");
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [addEmailCallback, setAddEmailCallback] = useState(null);
  const [sendInvitesCallback, setSendInvitesCallback] = useState(null);
  const [canEdit, setCanEdit] = useState(() => canEditRecord(poolData));

  useEffect(() => { setCanEdit(canEditRecord(poolData)); }, [poolData]);

  const handleClose = useCallback(() => {
    setCurrentTab("edit");
    setFormData(null);
    setErrors({});
    onClose();
  }, [onClose]);

  const handleTeamStatusChange = useCallback((teams) => {
    if (onTeamStatusChange) onTeamStatusChange(teams);
  }, [onTeamStatusChange]);

  const handleSave = useCallback(async () => {
    if (!formData || isSubmitting) return;

    const validationErrors = validateFormData(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      if (mode === "insert") {
        await savePoolAndInvite(formData);
        handleClose();
      } else if (mode === "edit") {
        await updatePoolDetails(formData, poolData);
        if (refreshData) await refreshData();
        handleClose();
      }
    } catch (error) {
      await logError({ filePath: 'Pool/PoolDetailsModal.js', functionName: 'handleSave', errorType: 'Save Data Error', errorMessage: error.message, stackTrace: error.stack });
      console.error("Failed to save data:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, mode, refreshData, handleClose, poolData, isSubmitting]);

  const handleAddEmail = useCallback(() => { if (addEmailCallback) addEmailCallback(); }, [addEmailCallback]);

  const handleSendInvites = useCallback(async () => {
    if (sendInvitesCallback) {
      setIsSubmitting(true);
      try { await sendInvitesCallback(); handleClose(); }
      finally { setIsSubmitting(false); }
    }
  }, [sendInvitesCallback, handleClose]);

  const setAddEmailCallbackMemoized = useCallback((fn) => {
    setAddEmailCallback(() => fn);
  }, []);

  const setSendInvitesCallbackMemoized = useCallback((fn) => {
    setSendInvitesCallback(() => fn);
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h2 className="text-lg font-bold text-gray-900">
            {mode === "insert" ? "Create Pool" : (poolData?.name || "Pool Settings")}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-all"
          >
            <IoCloseOutline className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto relative">
          {isSubmitting && currentTab !== "manage" && (
            <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          )}

          {!isPoolOwner ? (
            <PoolForm
              handleClose={handleClose} poolData={poolData} mode={mode}
              isPoolOwner={isPoolOwner} setFormData={setFormData} canEdit={canEdit}
              setCanEdit={setCanEdit} activeTab={currentTab} errors={errors} setErrors={setErrors}
            />
          ) : (
            <ModalContent
              handleSave={handleSave} handleClose={handleClose} poolData={poolData}
              mode={mode} isPoolOwner={isPoolOwner} setFormData={setFormData}
              currentTab={currentTab} setCurrentTab={setCurrentTab} errors={errors}
              setErrors={setErrors} onTeamStatusChange={handleTeamStatusChange}
              onAddEmail={setAddEmailCallbackMemoized}
              onSendInvites={setSendInvitesCallbackMemoized}
              isSubmitting={isSubmitting} setCanEdit={setCanEdit}
            />
          )}
        </div>

        {/* Footer / Nav */}
        {currentTab !== "manage" && (
          <PoolModalNavBar
            activeSection={currentTab} isPoolOwner={isPoolOwner} poolRecord={poolData}
            handleSave={handleSave} mode={mode} canEdit={canEdit}
            showSaveButton={currentTab === "edit"} showInviteButtons={currentTab === "invite"}
            onAddEmail={handleAddEmail} onSendInvites={handleSendInvites} isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(ContentModal);
